// eslint-disable-file sonarjs/cognitive-complexity
/** @format */
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	SimpleChanges
} from '@angular/core';

import { first, takeUntil } from 'rxjs/operators';

import { CertonaService } from '@product-recommendations/data-access';
import { BaseAddOnProductParserComponent } from '@shared/components';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import {
	AnalyticsService,
	EmitEvent,
	EventBusService,
	Events,
	Scene7Service,
	StoreService,
	UtilityService
} from '@shared/utils/services';

import { Store as NgRxStore } from '@ngrx/store';

import {
	AddOnCard,
	ATCEvent,
	ATCEventChildSku,
	ATCEventSkuData,
	ATCItem,
	Chain,
	IATC,
	ISkuLocation,
	KillSwitches,
	LaunchConfig,
	PostATCEvent,
	ProductSku,
	ProductStyle,
	SkuDefiningAttribute
} from '@shared/data-access/interfaces';
import { Subject } from 'rxjs';

import { selectIsProductAvailable } from '@pdp/add-to-cart/components';
import { ATC_BUTTON_TEXTS, selectShipMode, setFBTAirballInfo } from '@pdp/add-to-cart/data-access';
import { IProductInventory } from '@pdp/attributes/data-access';
import { selectEcommInventory, selectResolvedSku, setFBTProducts } from '@pdp/data-access/state';
import { CartService, ProductService } from '@shared/data-access/services';
import { Scene7MediaSetResponse } from '@shared/media/data-access';
import { ProductSelectionService } from '@pdp/data-access/services';

@Component({
    selector: 'pdp-frequently-bought-together',
    templateUrl: './frequently-bought-together.component.html',
    styleUrls: ['./frequently-bought-together.component.scss'],
    standalone: false
})
export class FrequentlyBoughtTogetherComponent
	extends BaseAddOnProductParserComponent
	implements OnInit, OnDestroy, OnChanges
{
	/**
	 * @description
	 *  Currently selelcted color attribute
	 */
	@Input() selectedColorAttribute = '';

	/**
	 * @description
	 *  Currently selected color attribute unformatted
	 */
	@Input() unformattedSelectedColorAttribute = '';

	@Input() killSwitches: KillSwitches;

	/**
	 * @description
	 *  Main product in the form of a product card
	 */
	rootProduct: AddOnCard<ProductSku>;

	/**
	 * @description
	 * ATC button configuration object
	 */
	buttonConfig: { disabled: boolean, text: string; variant: string; } = {
		text: '',
		variant: 'primary',
		disabled: false
	};

	/**
	 * @description
	 * Number of sku resolved product cards that are selected
	 */
	selectedProductCount: number;

	/**
	 * @description
	 * Array of sku resolved product cards that are selected
	 */
	resolvedATCProducts: IATC[] = [];

	/**
	 * @description
	 * Combined offerPrice of all products that are sku resolved and selected
	 */
	totalPrice = 0;

	/**
	 * @description
	 * Keeps track of whether this components ATC button was clicked
	 * Helps for showing the user cart add progression and for post ATC event logic
	 */
	addToCartClicked: boolean;

	/**
	 * @description
	 * Controls ATC success messaging
	 */
	addToCartSuccess: boolean;

	/**
	 * @description
	 * Keeps track of the user has opted to ship to home or BOPIS
	 */
	rootProductShipModeBopis: boolean;

	/**
	 * @description
	 * Keeps track of whether the main product is available to purchase or not
	 */
	rootProductAvailableToPurchase: boolean;

	/**
	 * @description
	 * Keeps track of if the root image is available from Scene7
	 */
	rootProductImageUnavailable = false;

	/**
	 * @description
	 * Used to keep cards unique in case there are multipe FBT component on one page
	 */
	randomUUIDForProductCard: string;

	/**
	 * @description
	 * Keeps track of whether the BaseAddOnProductParserComponent was able to locate the recommendations
	 */
	recommendationsFound: boolean;

	/**
	 * @description
	 * Show if color attribute does not match the selected color attribute
	 */
	showColorAttributeLabel = false;

	/**
	 * @description
	 * Keeps track of whether the root product sku has been resolved
	 */
	isSkuResolved = false;

	atcButtonTexts = ATC_BUTTON_TEXTS;

	/**
	 * @description
	 * Select options error message
	 */
	readonly SELECT_OPTIONS_ERROR_MESSAGE = 'Please Select Options Above';

	/**
	 * @description
	 * Unavailable Image URL
	 */
	readonly UNAVAILABLE_IMAGE_URL = 'https://images.dickssportinggoods.com/assets/icons/dsg/ImageUnavailablePDPSwatch.svg?qlt=70&wid=350&fmt=webp&op_sharpen=1';

	/**
	 * @description
	 * returns is lands boolean
	 */
	get isPL(): boolean {
		return this.chain.chainIdentifierAbbr === 'pl';
	}

	/**
	 * @description
	 * Keeps track of isFBTImprovementsEnabled flag/killswitch
	 */
	isFBTImprovementsEnabled: boolean;

	pdpProduct: ProductStyle | Record<string, never>;

	inventoryMapEcomm;

	private destroy$ = new Subject<void>();

	constructor(
		productService: ProductService,
		productContainerService: CertonaService,
		chain: Chain,
		protected analyticsService: AnalyticsService,
		@Inject(PLATFORM_ID) protected platformId: Object,
		@Inject(DOCUMENT) private document: Document,
		private cartService: CartService,
		private eventBusService: EventBusService,
		private scene7Service: Scene7Service,
		private utilityService: UtilityService,
		private changeDetectorRef: ChangeDetectorRef,
		private storeService: StoreService,
		private productSelectionService: ProductSelectionService,
		private ngrxStore: NgRxStore
	) {
		super(platformId, chain, analyticsService, productService, productContainerService);
	}

	ngOnInit(): void {
		this.isFBTImprovementsEnabled = this.utilityService.checkKillSwitch(
			'isFBTImprovementsEnabled',
			this.killSwitches,
			this.chain.chainIdentifierAbbr
		);

		if (isPlatformBrowser(this.platformId)) {
			// Default Certona title
			// Will be updated if certona provides dynamic title
			this.certonaTitle = 'Frequently Bought Together';
			this.numberOfCardsToShow = 2; // Default number of cards to show
			this.numColorsToShow = 999; // Default number of colors to show

			this.randomUUIDForProductCard = this.utilityService.generateUUID();

			this.pdpProduct = this.productSelectionService.getProductStyleData();

			this.product = this.pdpProduct;

			this.ngrxStore.select(selectEcommInventory).subscribe((ecommInventoryData: IProductInventory[]) => {
				if (ecommInventoryData) {
					// Set the inventory map
					this.inventoryMapEcomm = ecommInventoryData;
					if (this.pdpProduct) {
						// convert passed in product to addOnCardData
						this.rootProduct = {
							...this.generateDefaultRoot(this.pdpProduct)
						};
						if (this.rootProduct.skuItem && this.rootProduct.selected) {
							this.rootProductAvailableToPurchase = true;
							this.setItemsSelectedAndResolved();
							this.setButtonConfig();
						}
						this.listenForAttributeChanges();
						this.createProductAvailabilityEventListener();
					}
				}
			});

			super.initCertona();
			this.createRecommendationListener();

			this.checkRootImageAvailable();
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		// Update the image for color changes regardless of whether sku is resolved
		if (
			this.rootProduct &&
			changes?.selectedColorAttribute?.currentValue !==
				changes?.selectedColorAttribute?.previousValue &&
			!changes.selectedColorAttribute.firstChange
		) {
			const inWidth = 264;
			const inHeight = 264;
			const inParameters = this.isFBTImprovementsEnabled
				? { wid: inWidth, hei: inHeight }
				: {};
			const inImagePreset = this.isFBTImprovementsEnabled ? '' : '$DSG_ATCIMG$';
			this.rootProduct.imageUrl = this.scene7Service.getProductImageUrl(
				this.pdpProduct.partNumber,
				'ProductBean',
				this.selectedColorAttribute,
				inImagePreset,
				inParameters
			);

			this.showColorAttributeInRootProduct(this.rootProduct);
			this.checkRootImageAvailable();
		}
	}

	/**
	 * Check if the root image is available in the Product Set from Scene7
	 */
	checkRootImageAvailable(): void {
		this.scene7Service
			.getProductImageSet$(
				this.pdpProduct.partNumber,
				this.selectedColorAttribute,
				'_',
				'_is',
				0
			)
			.subscribe((mediaSetResponse: Scene7MediaSetResponse) => {
				this.rootProductImageUnavailable = (!mediaSetResponse?.item);
			});
	}

	/**
	 * Determine if the color attribute label should be shown in the root product card
	 * @param rootProduct - The root product card
	 */
	showColorAttributeInRootProduct(rootProduct) {
		if (rootProduct?.skuItem?.definingAttributes) {
			const selectedColor = rootProduct.skuItem.definingAttributes.find(
				(attr) => attr.name.toLowerCase() === 'color'
			);
			this.showColorAttributeLabel = selectedColor ? selectedColor.value !== this.unformattedSelectedColorAttribute : false;
		}
	}

	/**
	 * @description
	 * Iterates through all product cards and checks at rootProduct to determine which products are selected, resolved
	 * and generates a total resolved sku price
	 */
	setItemsSelectedAndResolved(): void {
		// Reset numbers because we parse everything anyway to find what the change effected
		this.totalPrice =
			this.rootProductAvailableToPurchase &&
			this.rootProduct?.skuItem &&
			this.rootProduct?.selected
				? this.rootProduct.skuItem.prices.offerPrice
				: 0;

		// Calculate number of products for pdp product
		this.selectedProductCount = this.rootProduct && this.rootProduct.selected ? 1 : 0;
		this.resolvedATCProducts =
			this.rootProduct && this.rootProductAvailableToPurchase && this.rootProduct.selected
				? [this.generateATCObject(this.rootProduct, false, this.rootProductShipModeBopis)]
				: [];

		for (
			let productCardsIndex = 0, productCardLength = this.cardDataList.length;
			productCardsIndex < productCardLength;
			productCardsIndex++
		) {
			const currentProduct = this.cardDataList[productCardsIndex] as AddOnCard<ProductSku>;

			if (currentProduct.selected && currentProduct.skuItem) {
				this.selectedProductCount = this.selectedProductCount + 1;
				this.resolvedATCProducts.push(this.generateATCObject(currentProduct, true, false));
				this.totalPrice = this.totalPrice + currentProduct.skuItem.prices.offerPrice;
			}
		}
		// Update our ATC button text
		this.setButtonConfig();
	}

	/**
	 * @description
	 * Set buttonConfig object fields
	 */
	setButtonConfig(): void {
		if (this.selectedProductCount === 0) {
			this.buttonConfig.variant = 'secondary';
			this.buttonConfig.text = ATC_BUTTON_TEXTS.atc;
			this.buttonConfig.disabled = true;
		} else if (this.selectedProductCount === this.resolvedATCProducts.length) {
			this.buttonConfig.variant = 'primary';
			this.buttonConfig.disabled = false;
			this.buttonConfig.text = ATC_BUTTON_TEXTS.atc;
		} else {
			this.buttonConfig.variant = 'primary';
			this.buttonConfig.text = ATC_BUTTON_TEXTS.atc;
			this.buttonConfig.disabled = false;
		}
	}

	toggleProductSelection(product: AddOnCard, event: MouseEvent, isRootProduct = false): void {
		if (!product.skuItem && !product.selected) {
			product.error = true;
			product.errorMessage = this.SELECT_OPTIONS_ERROR_MESSAGE;
			this.scrollToElementById(`${product.id}${this.randomUUIDForProductCard}`);

			if (event?.target && 'checked' in event.target) {
				event.target.checked = false;
			}
			return;
		} else if (isRootProduct && !this.rootProductAvailableToPurchase) {
			product.error = true;
			product.errorMessage = this.rootProductShipModeBopis
				? 'Not available to pick up in store'
				: 'Not available to ship';

			if (event?.target && 'checked' in event.target) {
				event.target.checked = false;
			}
			return;
		}

		product.selected = !product.selected;
		product.error = false;
		product.errorMessage = null;

		this.setItemsSelectedAndResolved();
		this.changeDetectorRef.detectChanges();
	}

	/**
	 * @description
	 * Checks if all selected products are sku resolved
	 * Will add to cart if all selected products are sku resolved
	 * Otherwise it will generate option errors and scroll the window to said errors
	 */
	addProductsToCart(): void {
		if (this.selectedProductCount !== this.resolvedATCProducts.length) {
			if (
				this.rootProduct &&
				this.rootProduct.selected &&
				this.rootProductAvailableToPurchase === false
			) {
				this.scrollToErrorsAndEmitUnsuccessfulATCEvent(
					this.rootProduct.skuItem,
					'shippingOptions'
				);
			}

			for (
				let productArrayIndex = 0, length = this.cardDataList.length;
				productArrayIndex < length;
				productArrayIndex++
			) {
				const currentProduct = this.cardDataList[productArrayIndex];

				if (!currentProduct.skuItem && currentProduct.selected) {
					currentProduct.error = true;
				}
			}
		} else {
			const launchConfig: LaunchConfig = {
				isLaunch: false,
				isSecureLaunch: false,
				launchCookies: [],
				cartHeader: {},
				handshakeParam: {},
				allowQueryParamOverride: false
			};

			const atcInitiateEvent: ATCItem = {
				atcObject: this.resolvedATCProducts,
				launchConfig,
				retries: 1,
				lowStockMessageShown: false
			};

			this.addToCartClicked = true;
			this.cartService.addToCart(atcInitiateEvent);
		}
	}

	openQuickviewPanel(product, isRoot = false): void {
		const ecode = product.id;
		const sku = product.skuItem?.partNumber;
		const rootSku = isRoot && this.isSkuResolved ? sku : undefined;

		const productQuickViewParams = {
			skuPartNumber: isRoot ? rootSku : sku,
			defaultColor: isRoot ? this.unformattedSelectedColorAttribute : undefined,
			additionalData: {}
		};

		if(!isRoot && product?.pdpSeoUrl) {
			//capture recid from url
			const recidMatch = product?.pdpSeoUrl.match(/recid=([^&]+)/);
			productQuickViewParams.additionalData = recidMatch ? recidMatch[1] : '';
		}

		window.quickview.openProductQuickView(ecode, 'certona', productQuickViewParams);
	}

	/**
	 * @description
	 * Get the formatted color attribute
	 * @param atrributes - List of attributes
	 * @returns - Formatted color attribute
	 */
	getFormattedColorAttribute(atrributes = [] as SkuDefiningAttribute[]): string {
		return atrributes.find((attr: SkuDefiningAttribute) => attr.name.toLowerCase() === 'color')?.value?.replace(/\W+/g, '_') || '';
	}

	showAttributes(productId: string): void {
		if (!productId) {
			return;
		}

		this.hideAttributesForProduct[productId] = false;
		this.analyticsService.triggerAnalyticsEvent('ShowProductOptions');
	}

	/**
	 * @description
	 * Listens for attribute selection changes and proceeds to update rootProduct as needed
	 */
	listenForAttributeChanges(): void {
		this.ngrxStore
			.select(selectResolvedSku)
			.pipe(takeUntil(this.destroy$))
			.subscribe((resolvedSku) => {
				this.isSkuResolved = !!resolvedSku;
				this.showColorAttributeLabel = !this.isSkuResolved;
				// Resolved product logic
				if (!resolvedSku) {
					this.rootProduct = Object.assign(
						this.rootProduct,
						this.generateDefaultRoot(this.pdpProduct)
					);
					this.setItemsSelectedAndResolved();
				} else {
					// set resolved sku
					this.rootProduct.skuItem = resolvedSku;

					// Remove any error messages
					this.rootProduct.errorMessage = undefined;

					// update price so it reflects on the product card
					this.rootProduct.shownPrice = {
						isSeePriceInCart: resolvedSku.prices.mapPriceIndicator === 1,
						isRangePrice: false,
						lowestPrice: resolvedSku.prices.offerPrice,
						highestPrice: undefined
					};

					// In case values are changed from ATC
					this.rootProduct.buttonText = undefined;
					this.rootProduct.disable = false;
					this.rootProduct.state = 'active';
					this.rootProduct.selected = true;

					this.setItemsSelectedAndResolved();
				}
			});
	}

	/**
	 * @description
	 * @param currentProduct - Addon object that needs to be converted to ATC object
	 * @param isProductAddOn - Determine if atc object is for add-on product
	 * @returns - Returns an object with the fields necessary to ATC
	 */
	generateATCObject(currentProduct: AddOnCard<ProductSku>, isProductAddOn: boolean, isBopis = false): IATC {
		return {
			ATCOriginated: 'PDP Shop The Look',
			productEcode: currentProduct.id,
			productTitle: currentProduct.title,
			skuOfferPrice: currentProduct.skuItem?.prices?.offerPrice,
			skuListPrice: currentProduct.skuItem?.prices?.listPrice,
			skuId: currentProduct.skuItem?.partNumber,
			skuDefAttributes: currentProduct.skuItem?.definingAttributes,
			skuCatentryId: currentProduct.skuItem?.catentryId,
			quantity: 1,
			isProductAddOn,
			isCustomizable: false,
			isBopis
		};
	}

	/**
	 * @description
	 * @param pdpProduct - Product object that needs to be converted to an AddOnCard with default values
	 * @returns - Returns an AddOnCard object with the default fields
	 */
	generateDefaultRoot(pdpProduct: ProductStyle | Record<string, never>): AddOnCard<ProductSku> {
		// If all attributes are not yet selected we only show the color attribute label in the root product card
		this.showColorAttributeLabel = true;

		// Resolve sku by finding root product sku that has ecomm inventory
		const productSkuData = this.productSelectionService.getProductSkusData();

		// Find the index of the root product sku that has ecomm inventory
		const rootProductIventoryIndex = productSkuData.findIndex((sku) => {
			if (!sku.definingAttributes) return false;
			const skuColor = sku.definingAttributes.find(
				(attr) => attr.name.toLowerCase() === 'color'
			);
			if (skuColor?.value === this.unformattedSelectedColorAttribute) {
				const ecommSkuLocation = this.storeService.getSkuInventoryFromLocations(
					this.inventoryMapEcomm,
					sku.partNumber
				);
				return this.skuInventoryCheck(sku, ecommSkuLocation);
			}
		});

		// Get the sku of the root product if has ATS otherwise get the first sku that has ATS
		const initialSkuWithATS = rootProductIventoryIndex === -1 ? productSkuData.find((sku) => {
			const ecommSkuLocation = this.storeService.getSkuInventoryFromLocations(
				this.inventoryMapEcomm,
				sku.partNumber
			);
			return this.skuInventoryCheck(sku, ecommSkuLocation);
		}) : productSkuData[rootProductIventoryIndex];


		const selectedColor = this.selectedColorAttribute.replace(/\W+/g, '_');
		const inWidth = 264;
		const inHeight = 264;
		const inParameters = this.isFBTImprovementsEnabled ? { wid: inWidth, hei: inHeight } : {};
		const inImagePreset = this.isFBTImprovementsEnabled ? '' : '$DSG_ATCIMG$';
		return {
			id: pdpProduct.partNumber,
			title: pdpProduct.name,
			skuItem: initialSkuWithATS,
			defAttributes: [],
			imageUrl: this.scene7Service.getProductImageUrl(
				pdpProduct.partNumber,
				'ProductBean',
				selectedColor,
				inImagePreset,
				inParameters
			),
			buttonText: undefined,
			disable: false,
			state: 'active',
			rank: -1, // init to -1 so if something goes wrong while setting data we wont display
			pdpSeoUrl: undefined,
			availableCombinations: [],
			error: true,
			errorMessage: initialSkuWithATS ? undefined : this.SELECT_OPTIONS_ERROR_MESSAGE,
			productPrice: undefined,
			shownPrice: {
				isSeePriceInCart: false,
				isRangePrice: pdpProduct.prices.maxofferprice !== pdpProduct.prices.minofferprice,
				lowestPrice: Number(pdpProduct.prices.minofferprice),
				highestPrice: Number(pdpProduct.prices.maxofferprice)
			},
			currentPdpProduct: true,
			selected: Boolean(initialSkuWithATS)
		};
	}

	/**
	 * Fires an unsuccessful ATC event on to the bus for other components and scrolls
	 * the user up to the errors on the page
	 *
	 * Event Fired:
	 * AddToCartEvent
	 *
	 * @param sku - Sku of product that produced the ATC error
	 * @param [errorID=defErrors] - The id of the div that holds the ATC error
	 */
	scrollToErrorsAndEmitUnsuccessfulATCEvent(sku: ProductSku, errorID = 'defErrors') {
		this.scrollToElementById(errorID);

		// emit un-resolved event
		this.eventBusService.emit(
			new EmitEvent(Events.AddToCartEvent, {
				success: false,
				skuData: {
					imageURL: this.rootProduct.imageUrl,
					quantity: 1,
					price: sku === undefined ? '' : sku.prices.offerPrice,
					skuID: sku === undefined ? '' : sku.partNumber,
					title: this.rootProduct.title,
					definingAttributes: sku === undefined ? '' : sku?.definingAttributes || '',
					personalizationData: {},
					customizationData: {}
				},
				mainItem: true
			} as ATCEvent)
		);
	}

	/**
	 * @description Scrolls to element by using the given id argument
	 * @param elementId - The id of the div that we are attempting to scroll to
	 */
	scrollToElementById(elementId: string): void {
		// Get a reference to the error message Element
		const errorMessageElement = this.document.getElementById(elementId);

		// Scroll to error message Element
		this.utilityService.scrollToElement(errorMessageElement, {
			behavior: 'smooth',
			block: 'center',
			inline: 'nearest'
		});
	}

	createRecommendationListener() {
		this.recommendationsListener
			.pipe(first())
			.subscribe((res: { recommendationsFound: boolean }) => {
				this.recommendationsFound = res.recommendationsFound;
				if (this.recommendationsFound) {
					this.createPostATCEventListener();
					let skuItemFound = 0;
					const inWidth = 264;
					const inHeight = 264;
					const inQuality = 80;
					const inOpSharpen = 1;
					const inParameters = {
						wid: inWidth,
						hei: inHeight,
						qlt: inQuality,
						op_sharpen: inOpSharpen
					};
					// Iterate through card list and see if any skus has been resolved
					for (let i = 0, len = this.slicedCardDataList.length; i < len; ++i) {
						const product = this.slicedCardDataList[i] as AddOnCard<ProductSku>;

						if (product.skuItem) {
							++skuItemFound;
							product.selected = true;
						}

						if (this.isFBTImprovementsEnabled) {
							// in new FBT we should never gray out any FBT items
							product.selected = true;

							// New FBT needs bigger image size
							let selectedColor = '';
							if (product.skuItem) {
								const attributes = product.skuItem.definingAttributes;
								selectedColor = this.getFormattedColorAttribute(attributes) || '';
							} else if (product.shownColorValues) {
								// Handle the occasional FBT rec that comes back without a specific SKU
								selectedColor = product.shownColorValues
									.split(',')[0]
									.replaceAll(' ', '_');
							}
							product.imageUrl = this.scene7Service.getProductImageUrl(
								product.id,
								'ProductBean',
								selectedColor,
								'',
								inParameters
							);
						}
					}

					if (skuItemFound > 0) {
						this.setItemsSelectedAndResolved();
					}

					// Component not showing for unless we force change detection
					this.changeDetectorRef.detectChanges();
				}
			});
	}

	private checkAddonsATCSuccess(
		addOnCards: AddOnCard<ProductSku>[],
		postATCEventMap: Record<string, PostATCEvent>
	) {
		const childSkuData: ATCEventChildSku[] = [];
		let addOnAdded: boolean;
		// Iterate through our list of add on to see which ones have successfully been added to cart
		// else if an add on has not been successfully added then show an error
		for (const addOn of addOnCards) {
			const skuItem = addOn.skuItem;

			if (
				skuItem &&
				postATCEventMap[skuItem.partNumber]?.success &&
				postATCEventMap[skuItem.partNumber]?.atcObject.isProductAddOn
			) {
				addOnAdded = true;
				childSkuData.push({
					title: addOn.title,
					definingAttributes: addOn.skuItem?.definingAttributes || [],
					skuId: addOn.skuItem.partNumber,
					productId: addOn.id,
					price: addOn.skuItem.prices.offerPrice
				});
			} else if (
				skuItem &&
				postATCEventMap[skuItem.partNumber]?.atcObject.isProductAddOn &&
				!postATCEventMap[skuItem.partNumber]?.success
			) {
				addOn.errorMessage = 'Airball: Please Try Again';
				addOnAdded = false;
			}
		}

		return { addOnAdded, childSkuData };
	}

	private getATCEventPrimarySku(rootProductAdded: boolean, childSkuData: ATCEventChildSku[]): ATCEventSkuData {
		if (rootProductAdded) {
			return {
				imageURL: this.rootProduct.imageUrl,
				quantity: 1,
				price: this.rootProduct?.skuItem?.prices?.offerPrice,
				skuID: this.rootProduct?.skuItem?.partNumber,
				title: this.rootProduct?.title,
				definingAttributes: this.rootProduct?.skuItem?.definingAttributes,
				personalizationData: {},
				customizationData: {}
			};
		}

		const firstProduct = childSkuData.shift();
		const color = firstProduct?.definingAttributes?.find(
			(attr: SkuDefiningAttribute) => attr.name.toLowerCase() === 'color'
		);
		const inWidth = 264;
		const inHeight = 264;
		const inParameters = this.isFBTImprovementsEnabled ? { wid: inWidth, hei: inHeight } : {};
		const inImagePreset = this.isFBTImprovementsEnabled ? '' : '$DSG_ATCIMG$';
		return {
			// Generate new image with color value but replace all non-word characters (including spaces) with an underscore
			imageURL: this.scene7Service.getProductImageUrl(
				firstProduct.productId,
				'ProductBean',
				color?.value?.replace(/\W+/g, '_') || '',
				inImagePreset,
				inParameters
			),
			quantity: 1,
			price: firstProduct.price,
			skuID: firstProduct.skuId,
			title: firstProduct.title,
			definingAttributes: firstProduct?.definingAttributes,
			personalizationData: {},
			customizationData: {}
		};
	}

	createPostATCEventListener() {
		this.eventBusService.on(Events.PostATCEvent, (postATCEvent: PostATCEvent[]) => {
			if (!this.addToCartClicked) {
				return;
			}

			const postATCEventMap: { [key: string]: PostATCEvent } = {}; // Creating a map in order to do a constant lookup of skuId
			let rootProductAdded: boolean;

			// Create Post ATC Event Map
			for (let i = 0, len = postATCEvent.length; i < len; i += 1) {
				const event = postATCEvent[i];
				postATCEventMap[event.atcObject.skuId] = event;
			}

			// Check if rootProduct exists and if it have successfully been added to cart
			// else if rootProduct exists and has not been successfully been added to cart then show an atc error
			if (
				this.rootProduct.skuItem &&
				postATCEventMap[this.rootProduct.skuItem.partNumber]?.success
			) {
				rootProductAdded = true;
			} else if (
				this.rootProduct?.skuItem &&
				postATCEventMap[this.rootProduct.skuItem.partNumber] &&
				!postATCEventMap[this.rootProduct.skuItem.partNumber].success
			) {
				this.rootProduct.errorMessage = 'Airball: Please Try Again';
				rootProductAdded = false;
			}

			const { addOnAdded, childSkuData } = this.checkAddonsATCSuccess(
				this.cardDataList as AddOnCard<ProductSku>[],
				postATCEventMap
			);

			// If this is the shop the look experience then emit an AddToCart event
			if (rootProductAdded || addOnAdded) {
				this.ngrxStore.dispatch(setFBTAirballInfo({ success: true }));
				this.addToCartSuccess = true;
				setTimeout(() => {
					this.addToCartSuccess = false;
				}, DCSG_CONSTANTS.timeouts.xshort);

				const skuData = this.getATCEventPrimarySku(rootProductAdded, childSkuData);

				this.ngrxStore.dispatch(
					setFBTProducts({ FBTProducts: [skuData, ...childSkuData] })
				);

				// Fire success event
				this.eventBusService.emit(
					new EmitEvent(Events.AddToCartEvent, {
						success: true,
						skuData,
						childSkuData,
						mainItem: true
					} as ATCEvent)
				);
			} else {
				this.ngrxStore.dispatch(setFBTAirballInfo({ success: false }));
				// Fire error event
				this.eventBusService.emit(
					new EmitEvent(Events.AddToCartEvent, {
						success: false,
						skuData: undefined,
						childSkuData,
						mainItem: false
					} as ATCEvent)
				);
			}

			this.addToCartClicked = false;
		});
	}

	/**
	 * @description
	 * Creates ship mode event listener
	 */
	createProductAvailabilityEventListener(): void {
		this.ngrxStore.select(selectShipMode).subscribe((shipMode) => {
			this.rootProductShipModeBopis = shipMode === 'bopis';
		});

		this.ngrxStore
			.select(selectIsProductAvailable)
			.subscribe(({ isAvailable, isSkuResolved }) => {
				this.rootProductAvailableToPurchase = isAvailable;

				if (!isSkuResolved) {
					this.rootProduct.errorMessage = this.SELECT_OPTIONS_ERROR_MESSAGE;
				} else if (isAvailable === false) {
					this.rootProduct.errorMessage = this.rootProductShipModeBopis
						? 'Not available to pick up in store'
						: 'Not available to ship';
					this.rootProduct.selected = false;
				} else {
					this.rootProduct.selected = true;
					this.rootProduct.errorMessage = undefined;
				}

				this.setItemsSelectedAndResolved();
			});
	}

	/**
	 * @description
	 * Determines if a sku has inventory for the provided ISkuLocation object
	 *
	 * @param sku
	 * @param skuLocation
	 */
	skuInventoryCheck(sku: ProductSku, skuLocation: ISkuLocation) {
		if (
			typeof sku !== 'undefined' &&
			typeof skuLocation !== 'undefined' &&
			`${sku.partNumber}` === `${skuLocation.sku}`
		) {
			return skuLocation.inventory > 0;
		}

		return false;
	}

	productCardImageUnavailable(index: number): void {
		//prevent infinite loop in some cases if the URL is already set to the unavailable image
		if (this.slicedCardDataList[index].imageUrl != this.UNAVAILABLE_IMAGE_URL) {
			this.slicedCardDataList[index].imageUrl = this.UNAVAILABLE_IMAGE_URL;
		}
	}

	ngOnDestroy() {
		if (this.destroy$) {
			this.destroy$.next();
			this.destroy$.complete();
		}
	}

	trackByProduct(index: number, item: AddOnCard): string {
		return index.toString() + item.id;
	}

	trackByCardData(index: number, item: AddOnCard): string {
		return index.toString() + item.title;
	}
}
